import { itemFetch, orderSpecialHandling } from "@/apis/inventoryv2";
import { AnyObject } from "antd/es/_util/type";
import { Descriptions, Flex, Modal, Spin, Table, TableProps } from "antd";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OrderItemSpecialData, OrderItemV2, OrderItemLocation } from "@/types/inventoryV2";
import SText from "@/styles/SText";
import useInspactionEnum from "@/pages/Warehouse/pages/Inspection/hooks/useInspactionEnum";
import { numFormmater } from "@/pages/Requirement/util";
import { getLocationIdx } from "@/utils/wLocation";
import STableLayout from "@/styles/STableLayout";

interface SpecialHandleModalProps {
  open: boolean;
  orderItem: OrderItemV2;
  onClose: () => void;
}

interface Row extends OrderItemSpecialData {
  location: OrderItemLocation['location'] | null;
}

const SpecialHandleModal: FC<SpecialHandleModalProps> = ({ open, orderItem, onClose }) => {
  const { t } = useTranslation();
  const { getSpecialHandling } = useInspactionEnum();
  const [loading, setLoading] = useState<boolean>(false);
  const [item, setItem] = useState<OrderItemV2 | null>(null);
  const [rows, setRows] = useState<Row[]>([]);

  const initData = async () => {
    setLoading(true);
    try {
      // 先取订单项拿到 locations，才能按 orderItemId/lotItemLocationId 关联特殊处理记录
      const resItem = await itemFetch(orderItem.id);
      const resHandling = await orderSpecialHandling(undefined, orderItem.orderId);
      setItem(resItem);
      const locMap = new Map((resItem.locations || []).map(loc => [loc.locationId, loc.location]));
      console.log(">>", locMap);

      setRows(
        (resHandling.body || [])
          .filter(h => h.orderItemId === orderItem.id)
          .map(h => ({ ...h, location: locMap.get(h.locationId) ?? null }))
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) initData();
  }, [open, orderItem]);

  const columns: TableProps<Row>['columns'] = [
    {
      title: t('counting.locationIdx'),
      key: "locationNumber",
      width: 150,
      render: (_, record) => <SText text={getLocationIdx(record?.location)} />,
    },
    {
      title: t('inspection.specialHandlingState'),
      key: "specialHandlingState",
      width: 120,
      render: (_, record) => {
        let info = getSpecialHandling(record.specialHandlingState);
        return info && <SText tagMode color={info.color} text={info.label} />
      },
    },
    {
      title: t('inspection.specialHandlingRemark'),
      key: "specialHandlingRemark",
      render: (_, record) => <SText text={record.specialHandlingRemark} />,
    },
  ];

  const showItem = useMemo(() => item ?? orderItem, [item, orderItem]);

  return (
    <Modal title={t('inspection.specialHandlingAggregate')} open={open}
      onCancel={onClose} footer={null} width={800} destroyOnClose maskClosable={false}>
      <Spin spinning={loading}>
        <Flex vertical style={{ height: "60vh" }} gap={6}>
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label={t('order.inOutNo')}>{showItem.order?.number}</Descriptions.Item>
            <Descriptions.Item label={t('counting.quantity')}>{numFormmater(showItem.count)}</Descriptions.Item>
            <Descriptions.Item label={t('warehouse.batchNo')}>{showItem.lot}</Descriptions.Item>
            <Descriptions.Item label={t('product.code')}>{showItem.product?.code}</Descriptions.Item>
            <Descriptions.Item label={t('product.name')}>{showItem.product?.name}</Descriptions.Item>
            <Descriptions.Item label={t('product.uom')}>{showItem.product?.uom}</Descriptions.Item>
            <Descriptions.Item label={t('product.brand1')}>{showItem.product?.model}</Descriptions.Item>
          </Descriptions>
          <STableLayout
            rowKey="id"
            size="small"
            columns={columns as Array<AnyObject>}
            dataSource={rows}
            pagination={false}
            headerChildren={null}
            total={rows.length}
            extHeight={100}
          />
        </Flex>
      </Spin>
    </Modal>
  )
};

export default SpecialHandleModal;
